import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchApprovals } from "../utils/auth";
import { approvalBlocker } from "../utils/approvals";

// The queue of schedule changes staff have asked for. Admins approve or
// reject; everyone else watches their own requests here. Approving applies the
// change through the same code a hand edit uses — see App.js — so this view
// only decides, it doesn't edit the schedule itself.

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-200 text-gray-600",
};

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function ApprovalsView({ currentUser, players, groups, onApprove, onReject, onCancel, reloadKey }) {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const isAdmin = currentUser?.role === "admin";

  const load = useCallback(async () => {
    try {
      const rows = await fetchApprovals({ all: true });
      setRequests(rows);
      setError(null);
    } catch (e) {
      setRequests([]);
      setError(e.message || "Could not load the approval queue.");
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    (requests || []).forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [requests]);

  // Newest first: the queue is worked from the top, and history reads better
  // most-recent-first too.
  const visible = useMemo(
    () => (requests || []).filter(r => r.status === filter).slice().reverse(),
    [requests, filter]
  );

  async function act(request, fn) {
    setBusyId(request.id);
    try {
      await fn(request);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  if (requests === null) {
    return <p className="text-sm text-gray-500 p-6">Loading the approval queue…</p>;
  }

  return (
    <div className="p-4 max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Approvals</h2>
          <p className="text-sm text-gray-500">
            {isAdmin
              ? "Placements and group changes staff have asked for. Nothing here is part of the schedule until you approve it."
              : "Changes you've asked for. An admin has to approve them before they're part of the schedule."}
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "cancelled"].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-sm px-3 py-1.5 rounded-lg border capitalize transition-colors ${
              filter === s
                ? "bg-blue-600 border-blue-600 text-white font-medium"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {s} ({counts[s]})
          </button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">{error}</p>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">{filter === "pending" ? "✓" : "—"}</div>
          <p className="text-gray-500 text-sm">
            {filter === "pending" ? "Nothing is waiting for approval." : `No ${filter} requests.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(r => {
            const blocker = r.status === "pending" ? approvalBlocker(r, players, groups) : null;
            const busy = busyId === r.id;
            const isMine = r.requestedById === currentUser?.id;
            return (
              <div key={r.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800">{r.summary || r.type}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_STYLES[r.status] || "bg-gray-100 text-gray-600"}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Asked by {r.requestedBy || "someone"}{isMine && " (you)"} · {when(r.requestedAt)}
                  </div>
                  {r.decidedAt && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {r.status} by {r.decidedBy || "an admin"} · {when(r.decidedAt)}
                      {r.reason && ` · "${r.reason}"`}
                    </div>
                  )}
                  {blocker && (
                    <div className="text-xs text-red-700 mt-1">Can't be applied: {blocker}</div>
                  )}
                </div>

                {r.status === "pending" && (
                  <div className="flex flex-wrap gap-2">
                    {isAdmin && (
                      <>
                        <button
                          disabled={busy || Boolean(blocker)}
                          onClick={() => act(r, onApprove)}
                          title={blocker || "Approve and apply this change"}
                          className="text-sm bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => act(r, onReject)}
                          className="text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-40 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {isMine && (
                      <button
                        disabled={busy}
                        onClick={() => act(r, onCancel)}
                        className="text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-40 text-gray-600 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Withdraw
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
